import {
  Box,
  HStack,
  Text,
  useRadio,
  useRadioGroup,
  UseRadioProps,
} from '@chakra-ui/react';
import React, { PropsWithChildren } from 'react';

import { TypeUtils } from '../../framework/utils/UtilityTypes';
import { Pages } from '../constants/Page';
import { useSizeContext } from '../containers/SizeProvider';
import { useGlobalStateMachine } from '../GlobalStore';

/**
 * ラジオカード
 * @param props -
 * @returns JSX要素
 */
const RadioCard: React.FunctionComponent<PropsWithChildren<UseRadioProps>> = (
  props
) => {
  const { getCheckboxProps, getInputProps } = useRadio(props);

  return (
    <Box as="label">
      <input {...getInputProps()} />
      <Box
        {...getCheckboxProps()}
        cursor="pointer"
        borderWidth="1px"
        borderRadius="md"
        boxShadow="md"
        _checked={{
          bg: 'teal.600',
          color: 'white',
          borderColor: 'teal.600',
        }}
        _focus={{
          boxShadow: 'outline',
        }}
        paddingX={4}
        paddingY={2}
      >
        {props.children}
      </Box>
    </Box>
  );
};

/**
 * ラジオカードグループプロパティ
 */
type RadioCardGroupProps = { label?: React.ReactText };
/**
 * ラジオカードグループフックプロパティ
 */
type UseRadioCardGroupProps<V> = {
  defaultValue?: V;
  optionValues: Array<{ value: V; displayedValue: string }>;
  key?: Pages;
};
/**
 * ラジオカードグループフック
 * @param props -
 * @returns 選択されている値およびコンポーネントのリスト
 */
const useRadioCardGroup = <V extends string>({
  defaultValue,
  key,
  optionValues,
}: UseRadioCardGroupProps<V>) => {
  const { actions } = useGlobalStateMachine();
  const sizes = useSizeContext();
  const { value, getRootProps, getRadioProps } = useRadioGroup({
    name: 'radio-cards',
    defaultValue: defaultValue || undefined,
  });

  /**
   * ラジオカードグループ
   * @param label -
   * @returns
   */
  const RadioCardGroup: React.FunctionComponent<RadioCardGroupProps> = ({
    label,
  }) => (
    <HStack fontSize={sizes.fontSize.rawMessage} {...getRootProps()}>
      <Text>{label}:</Text>
      {optionValues.map((opt) => {
        const radioProps = getRadioProps({ value: opt.value });

        return (
          <RadioCard
            key={opt.value}
            {...radioProps}
            onChange={(e) => {
              if (!TypeUtils.isNil(key)) {
                actions.updatePageInfo({ [key]: { selected: opt.value } });
              }
              if (!TypeUtils.isNil(radioProps.onChange)) {
                radioProps.onChange(e);
              }
            }}
          >
            {opt.displayedValue}
          </RadioCard>
        );
      })}
    </HStack>
  );

  return [value, RadioCardGroup] as [
    V,
    React.FunctionComponent<RadioCardGroupProps>
  ];
};

export default useRadioCardGroup;
